import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserCheck, 
  Calendar,
  CreditCard,
  TrendingUp,
  DollarSign,
  Star
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import AdminLayout from '../common/AdminLayout';

interface DashboardStats {
  totalRegisteredTalent: number;
  totalRegisteredClients: number;
  totalActiveTalent: number;
  totalActiveClients: number;
  totalPremiumSubscribers: number;
  totalRevenue: number;
  totalBookings: number;
  completedBookings: number;
}

interface ChartData {
  userRegistrations: Array<{ date: string; talent: number; clients: number }>;
  bookings: Array<{ date: string; bookings: number; completed: number; cancelled: number }>;
  revenue: Array<{ date: string; revenue: string | number }>;
}

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  data: any;
}

interface UnreadSummary {
  total: number;
  notifications: number;
  contactSubmissions: number;
  serviceInquiries: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [chartPeriod, setChartPeriod] = useState('7days');
  const [recentNotifications, setRecentNotifications] = useState<NotificationItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadSummary, setUnreadSummary] = useState<UnreadSummary | null>(null);
  
  const { admin } = useAuth();

  // Fetch dashboard stats
  useEffect(() => {
    fetchDashboardStats();
  }, [dateRange]);

  // Fetch chart data
  useEffect(() => {
    fetchChartData();
  }, [chartPeriod]);

  // Fetch recent notifications and unread summary
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const [listRes, unreadRes] = await Promise.all([
          api.get(`/notifications/admin?limit=5`),
          api.get(`/notifications/admin/unread-count`)
        ]);
        if (listRes.data?.success) {
          setRecentNotifications(listRes.data.notifications || []);
        }
        if (unreadRes.data?.success) {
          setUnreadSummary(unreadRes.data.unreadCount);
        }
      } catch (e) {
        // Silent fail on dashboard extras
      } finally {
        setNotificationsLoading(false);
      }
    };
    loadNotifications();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.from) params.append('from', dateRange.from);
      if (dateRange.to) params.append('to', dateRange.to);

      const response = await api.get(`/dashboard/stats?${params.toString()}`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartLoading(true);
      const response = await api.get(`/dashboard/chart-data?period=${chartPeriod}`);
      if (response.data.success) {
        setChartData(response.data.chartData);
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    } finally {
      setChartLoading(false);
    }
  };

  // Updated chart colors for dark theme
  const colors = {
    primary: 'var(--accent-blue)',
    secondary: 'var(--accent-green)',
    light: 'var(--accent-purple)',
    dark: 'var(--accent-orange)',
    lighter: 'var(--text-light-gray)',
    white: 'var(--text-white)'
  };

  // Prepare data for combined user registrations chart
  const combinedUserData = chartData?.userRegistrations?.map(item => ({
    date: item.date,
    talent: item.talent || 0,
    clients: item.clients || 0
  })) || [];

  // Prepare data for bookings chart
  const bookingsChartData = chartData?.bookings?.map(item => ({
    date: item.date,
    total: item.bookings || 0,
    completed: item.completed || 0,
    cancelled: item.cancelled || 0
  })) || [];

  // Prepare data for revenue chart
  const revenueChartData = chartData?.revenue?.map(item => ({
    date: item.date,
    revenue: Number(item.revenue) || 0
  })) || [];

  // Prepare data for pie chart (user distribution)
  const userDistributionData = [
    { name: 'Talent', value: stats?.totalRegisteredTalent || 0, color: colors.primary },
    { name: 'Clients', value: stats?.totalRegisteredClients || 0, color: colors.secondary },
    { name: 'Premium', value: stats?.totalPremiumSubscribers || 0, color: colors.light }
  ];

  const statCards = [
    {
      title: 'Total Talent',
      value: stats?.totalRegisteredTalent || 0,
      icon: Users
    },
    {
      title: 'Total Clients',
      value: stats?.totalRegisteredClients || 0,
      icon: UserCheck
    },
    {
      title: 'Active Talent',
      value: stats?.totalActiveTalent || 0,
      icon: TrendingUp
    },
    {
      title: 'Premium Subscribers',
      value: stats?.totalPremiumSubscribers || 0,
      icon: Star
    },
    {
      title: 'Total Revenue',
      value: `$${stats?.totalRevenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign
    },
    
  ];
 
  const today=new Date().toISOString().split("T")[0]

  return (
    <AdminLayout title="Analytics Dashboard" subtitle="Overview of platform performance and metrics">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <h3 style={{ margin: 0, color: 'var(--text-white)' }}>Welcome back, {admin?.name}!</h3>
              <p style={{ margin: 0, color: 'var(--text-light-gray)', fontSize: '14px' }}>
                Here's what's happening with your SkillSeek platform today.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select
                value={chartPeriod}
                onChange={(e) => setChartPeriod(e.target.value)}
                className="form-control"
                style={{ width: '140px' }}
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
                <option value="1year">Last Year</option>
              </select>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="form-control"
                style={{ width: '150px' }}
              max={today}
    
              />
              <span style={{ color: 'var(--text-light-gray)' }}>to</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="form-control"
                style={{ width: '150px' }}
                max={today}
            
              />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stat-cards">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="stat-card">
                <div className="loading-spinner"></div>
              </div>
            ))
          ) : (
            statCards.map((card, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">
                  <card.icon size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{card.value}</div>
                  <div className="stat-label">{card.title}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Charts Section */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '25px', marginBottom: '25px' }}>
          {/* User Registrations Chart */}
          <div className="admin-card">
            <div className="card-header">
              <h4 className="card-title">User Registrations</h4>
            </div>
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={combinedUserData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-medium)" />
                  <XAxis dataKey="date" stroke="var(--text-light-gray)" fontSize={12} />
                  <YAxis stroke="var(--text-light-gray)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--secondary-black)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--text-white)'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="talent" 
                    stroke={colors.primary} 
                    strokeWidth={2}
                    name="Talent"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="clients" 
                    stroke={colors.secondary} 
                    strokeWidth={2}
                    name="Clients"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* User Distribution Pie Chart */}
          <div className="admin-card">
            <div className="card-header">
              <h4 className="card-title">User Distribution</h4>
            </div>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {userDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--secondary-black)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      color: 'var(--text-white)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent Notifications */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div className="card-header" style={{ alignItems: 'center' }}>
            <h4 className="card-title" style={{ margin: 0 }}>Recent Notifications</h4>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {unreadSummary && (
                <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'var(--text-medium)' }}>
                  <span><strong>{unreadSummary.total}</strong> Unread</span>
                  <span>|</span>
                  <span>Notif: {unreadSummary.notifications}</span>
                  <span>Contact: {unreadSummary.contactSubmissions}</span>
                  <span>Inquiries: {unreadSummary.serviceInquiries}</span>
                </div>
              )}
              <Link to="/notifications" className="btn-secondary" style={{ padding: '8px 12px' }}>View All</Link>
            </div>
          </div>
          {notificationsLoading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <div className="loading-spinner"></div>
            </div>
          ) : recentNotifications.length === 0 ? (
            <div style={{ padding: '20px', color: 'var(--text-medium)' }}>
              No recent notifications.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10, padding: '10px 0' }}>
              {recentNotifications.map((n) => (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--border-medium)', borderRadius: 8, background: n.is_read ? 'transparent' : '#f0f7ff' }}>
                  <span style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, background: 'var(--secondary-black)', color: 'var(--text-white)', textTransform: 'capitalize' }}>{n.type}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{n.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.message}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-medium)' }}>{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  );
};

export default Dashboard;